import * as React from "react";
import { PFTResults } from '../Shared/PFTAPITypes';
import { LocalizedDateTime, DateTimeAccuracy } from '../../misc/components/LocalizedDateTime';
import { PFTResultParameters } from '../Shared/PFTResultsParameters';
import { FixedCommentsSelectors } from '../Shared/FixedCommentsSelectors';
import { FixedCommentsState, ToggleSessionFixedCommentsVisibilityAction, ToggleTestFixedCommentsVisibilityAction, PageValidatedAction, LoadAnalysisSetOverreadAction } from "../Shared/UseFixedComments";
import { SelectedTestCommentsCount, SelectedSessionCommentsCount } from "../Shared/Selectors/FixedCommentSelectors";
import { CommentModals } from "../Shared/CommentsModals";
import { AcceptabilityState, ToggleTestAcceptabilityAction } from '../Shared/UseAcceptability';
import RepeatabilityTable from "../Shared/RepeatabilityTable";
import { DlcoTestAcceptabilityTable } from "./DlcoTestAcceptabilityTable";
import { useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";

export interface DlcoResultsTableProps {
    isViewOnly: boolean;
    isSiteAnalysisSet: boolean;
    resultData: PFTResults;
    fixedCommentsState: FixedCommentsState;
    fixedCommentsDispatch;
    acceptabilityState: AcceptabilityState;
    acceptabilityDispatch;
    shouldValidateFixedComments: boolean;
}

export const DlcoResultsTable = ({ isViewOnly, isSiteAnalysisSet, resultData, fixedCommentsState, fixedCommentsDispatch, shouldValidateFixedComments, acceptabilityState, acceptabilityDispatch }: DlcoResultsTableProps) => {
    const legacyClient = useWebPortalLegacyClient();

    React.useEffect(() => {
        const fetchOverreading = async () => {
            const analysisOverreading = await legacyClient.getDlcoAnalysisSetOverreading(resultData.AnalysisSetId);
            
            fixedCommentsDispatch(LoadAnalysisSetOverreadAction.create(analysisOverreading));
        }

        fetchOverreading();
    }, [resultData.AnalysisSetId, fixedCommentsDispatch])

    return (
        <>
            <table id="DlcoResultsTable" className="table table-striped">
                <thead>
                    <tr>
                        <th></th>
                        {
                            resultData.TestResults.sort((a, b) => a.TestNumber - b.TestNumber).map(t =>
                                <th key={`TestHeader${t.TestId}`} style={{ color: "#000" }}>{t.TestNumber}</th>
                            )}
                        <th>Best</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Time</td>
                        {
                            resultData.TestResults.sort((a, b) => a.TestNumber - b.TestNumber).map(t =>
                                <td key={`TestDateTime${t.TestId}`}>
                                    <LocalizedDateTime time={t.TestDateTime} accuracy={DateTimeAccuracy.Second} />
                                </td>
                            )}
                        <td></td>
                    </tr>
                    <PFTResultParameters isLoading={acceptabilityState.loadingBestValues} results={resultData} />
                    <RepeatabilityTable isLoading={acceptabilityState.loadingBestValues} results={acceptabilityState.spirometryResults} />
                    <DlcoTestAcceptabilityTable
                        isViewOnly={isViewOnly}
                        displayTechnicianAcceptability={!isSiteAnalysisSet}
                        testResults={acceptabilityState.spirometryResults.TestResults}
                        onAcceptabilityChange={(id, checked) => acceptabilityDispatch(ToggleTestAcceptabilityAction.create(id, checked))}
                    />
                    <FixedCommentsSelectors
                        addCommentsSuffix={true}
                        testResults={resultData.TestResults}
                        selectedTestCommentsCount={SelectedTestCommentsCount(fixedCommentsState.testFixedComments)}
                        selectedSessionCommentsCount={
                            SelectedSessionCommentsCount(fixedCommentsState.sessionFixedComments)
                        }
                        onTestFixedCommentClick={testId =>
                            fixedCommentsDispatch(
                                ToggleTestFixedCommentsVisibilityAction.create(testId)
                            )
                        }
                        onSessionFixedCommentClick={() =>
                            fixedCommentsDispatch(
                                ToggleSessionFixedCommentsVisibilityAction.create()
                            )
                        }
                    />
                    
                </tbody>
            </table>
            <CommentModals
                editable={!isViewOnly}
                sessionFixedComments={fixedCommentsState.sessionFixedComments}
                testFixedComments={fixedCommentsState.testFixedComments}
                setIsPageValidated={(isValidated) => fixedCommentsDispatch(PageValidatedAction.create(isValidated))}
                isPageValid={fixedCommentsState.isPageValidated}
                dispatch={fixedCommentsDispatch}
            />
        </>
    );
}
